import type { ReactNode } from 'react';

type Tone = 'info' | 'warn' | 'danger' | 'ok';

const TONE: Record<Tone, [string, string, string]> = {
  info: ['var(--color-surface)', 'var(--color-divider)', 'var(--color-neutral-600)'],
  warn: ['#FDF2CC', '#F5D98B', '#B54708'],
  danger: ['#FEE4E2', '#FECDCA', '#B42318'],
  ok: ['#D1FADF', '#A9E8C0', '#067647'],
};

/**
 * A stated consequence, not a decoration.
 *
 * Used where the product must tell a producer something it will NOT do for them
 * — a stale primary it refuses to re-point, a revision blocking matching. These
 * read as instructions rather than alarms, because the producer is being asked
 * to decide, not warned that something broke.
 */
export function Callout({
  tone = 'info',
  title,
  children,
  action,
  testId,
}: {
  tone?: Tone;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  testId?: string;
}) {
  const [bg, border, fg] = TONE[tone];
  return (
    <div
      data-testid={testId}
      style={{
        background: bg,
        border: `1px solid ${border}`,
        padding: '14px 16px',
        display: 'flex',
        gap: 14,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: fg }}>{title}</p>
        {children ? (
          <div style={{ margin: '6px 0 0', fontSize: 12.5, lineHeight: 1.6, color: 'var(--ink)' }}>
            {children}
          </div>
        ) : null}
      </div>
      {action ? <div style={{ flex: 'none' }}>{action}</div> : null}
    </div>
  );
}
